import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import CheckIcon2 from '~/icons/components/CheckIcon2';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { getFacetOptionRBKey } from '../../../utils/filters';

const DynamicFormattedMessage = FormattedMessage;

const FilterOptionsList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OptionItem = styled.li<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? '0.35' : '1')};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transition: opacity 0.2s ease;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
`;

const StyledCheckbox = styled.div<{ $checked: boolean }>`
  width: 16px;
  height: 16px;
  border: 1px solid ${({ theme }) => theme.colors.palette.shadow};
  border-radius: 2px;
  background-color: ${({ $checked, theme }) =>
    $checked ? theme.colors.palette.shadow : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.2s,
    border-color 0.2s;
  flex-shrink: 0;
  position: relative;
  margin-right: 4px;

  &::before {
    content: '';
    position: absolute;
    width: 32px;
    height: 32px;
    border-radius: 64px;
    border: 1px solid rgba(0, 0, 0, 0);
    background-color: transparent;
    transition: border-color 0.2s ease;
  }

  /* Gate both hover and active states on hover-capable devices — on touch,
     :active can linger after tap (especially when the DOM updates mid-tap),
     leaving the halo ring visible after toggling the checkbox. */
  @media (hover: hover) {
    ${OptionItem}:hover &::before {
      border-color: ${({ theme }) => theme.colors.palette.shadow30};
    }

    ${OptionItem}:active &::before {
      border-color: ${({ theme }) => theme.colors.palette.shadow40};
    }
  }

  ${HiddenCheckbox}:focus-visible + &::before {
    border-color: ${({ theme }) => theme.colors.palette.shadow40};
  }

  svg {
    display: ${({ $checked }) => ($checked ? 'block' : 'none')};
  }
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  user-select: none;
`;

const FilterOptionsDefaultCheckbox = ({
  facetName,
  options,
  onClick,
  isSelected,
  isDisabled,
}) => {
  return (
    <FilterOptionsList>
      {options.map((option) => {
        const facetValue = option.value;
        const selected = isSelected(facetValue);
        const disabled = isDisabled?.(facetValue);

        const handleToggle = () => {
          if (!disabled) {
            onClick(facetValue);
          }
        };

        return (
          <OptionItem
            key={facetValue}
            onClick={handleToggle}
            $disabled={disabled}
            aria-disabled={disabled || undefined}
            data-autotag={`filter-${facetName}-option`}
          >
            <HiddenCheckbox
              id={`${facetName}-${facetValue}`}
              name={facetValue}
              value={facetValue}
              checked={selected}
              disabled={disabled}
              onChange={handleToggle}
              onClick={(e) => e.stopPropagation()}
            />
            <StyledCheckbox $checked={selected}>
              <CheckIcon2 width={10} height={8} />
            </StyledCheckbox>
            <CheckboxLabel
              htmlFor={`${facetName}-${facetValue}`}
              onClick={(e) => e.stopPropagation()}
            >
              <DynamicFormattedMessage
                id={`grid_dynamic.${getFacetOptionRBKey(
                  facetName,
                  facetValue
                )}`}
                defaultMessage={option.display_name}
              />
            </CheckboxLabel>
          </OptionItem>
        );
      })}
    </FilterOptionsList>
  );
};

FilterOptionsDefaultCheckbox.propTypes = {
  facetName: PropTypes.string.isRequired,
  isDisabled: PropTypes.func,
  isSelected: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string,
      value: PropTypes.string,
    })
  ).isRequired,
};

export default FilterOptionsDefaultCheckbox;
